/**
 * DraftAddClauseDialog
 *
 * Modal for adding a single clause to a draft checklist. Copy of
 * AddClauseToChecklistDialog with draft API calls. Handles both new
 * topic creation and merging into existing topics.
 */

import {
    getAllClauses,
    getTopic,
    IApiAxiosError,
    IClause,
    IEnabledClauseSection,
} from "@/api";
import {
    addTopicToDraft,
    adminDraftQueryKeys,
    getDraft,
    updateDraftTopicClauses,
} from "@/api/clause-checklists/drafts";
import { TableFilterToolbar } from "@/components/admin/table/TableFilterToolbar";
import { Modal } from "@/components/shared/Modal";
import { Table } from "@/components/shared/table/Table";
import { PolicyTypeCell } from "@/components/shared/table/cells/PolicyTypeCell";
import { useFilterModel } from "@/hooks/useFilterModel";
import { Chip, Stack, Typography } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";

const columns: GridColDef<IClause>[] = [
    {
        field: "title",
        headerName: "Clause Name",
        flex: 2,
        minWidth: 200,
        renderCell: ({ row }) => (
            <Typography variant="body2" noWrap>
                {row.title}
            </Typography>
        ),
    },
    {
        field: "topics",
        headerName: "Topic",
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => {
            if (!row.topics || row.topics.length === 0) {
                return (
                    <Typography variant="body2" color="text.secondary">
                        No topic
                    </Typography>
                );
            }
            const primaryTopic = row.topics[0];
            return (
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2" noWrap>
                        {primaryTopic.name}
                    </Typography>
                    {row.topics.length > 1 && (
                        <Chip
                            label={`+${row.topics.length - 1}`}
                            size="small"
                            sx={{ fontSize: "0.65rem", height: 18 }}
                        />
                    )}
                </Stack>
            );
        },
    },
    {
        field: "policy_type",
        headerName: "Policy Type",
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => (
            <PolicyTypeCell policyType={row.policy_type} />
        ),
    },
];

interface DraftAddClauseDialogProps {
    open: boolean;
    onClose: () => void;
    draftId: number;
    excludeTopicIds: number[];
}

export const DraftAddClauseDialog = ({
    open,
    onClose,
    draftId,
    excludeTopicIds,
}: DraftAddClauseDialogProps) => {
    const [selectedClause, setSelectedClause] = useState<IClause | null>(null);
    const [rows, setRows] = useState<IClause[]>([]);

    const { filterModel, handleFilterModelChange, resetFilterModel } =
        useFilterModel();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const queryClient = useQueryClient();

    const assignedTopicIds = useMemo(
        () => new Set(excludeTopicIds),
        [excludeTopicIds]
    );

    const {
        mutate,
        isPending: mutationPending,
        error: mutationError,
        reset,
    } = useMutation({
        mutationFn: async () => {
            if (!selectedClause || selectedClause.topics.length === 0) {
                throw new Error("Selected clause has no assigned topic");
            }

            const targetTopic = selectedClause.topics[0];
            const topicId = targetTopic.id;

            const topicDetail = await getTopic(topicId);

            let topicClauseId: number | null = null;
            const clauseSections: (number | null)[] = [];

            for (const section of topicDetail.clauses_by_section) {
                const clauseInSection = section.clauses.find(
                    (c) => c.clause_id === selectedClause.id
                );
                if (clauseInSection) {
                    topicClauseId = clauseInSection.topic_clause_id;
                    clauseSections.push(
                        section.document_section_type?.id ?? null
                    );
                }
            }

            if (topicClauseId === null) {
                throw new Error("Clause not found in topic");
            }

            const newEnabledClauses: IEnabledClauseSection[] =
                clauseSections.map((sectionId) => ({
                    topic_clause_id: topicClauseId!,
                    document_section_type_id: sectionId,
                }));

            const topicExists = assignedTopicIds.has(topicId);

            if (!topicExists) {
                await addTopicToDraft(draftId, {
                    topic_id: topicId,
                    enabled_clauses: newEnabledClauses,
                });
            } else {
                // Fetch current draft to get existing enabled clauses
                const draft = await getDraft(draftId);
                const existingTopic = draft.topics.find(
                    (t) => t.topic_id === topicId
                );

                if (!existingTopic) {
                    throw new Error(`Topic ${topicId} not found in draft`);
                }

                if (existingTopic.enabled_clauses.length === 0) {
                    throw new Error(
                        "This clause is already enabled in the draft (all clauses enabled)"
                    );
                }

                const existingKeys = new Set(
                    existingTopic.enabled_clauses.map(
                        (ec) =>
                            `${ec.topic_clause_id}:${ec.document_section_type_id}`
                    )
                );

                const mergedEnabledClauses = [...existingTopic.enabled_clauses];

                for (const newEc of newEnabledClauses) {
                    const key = `${newEc.topic_clause_id}:${newEc.document_section_type_id}`;
                    if (!existingKeys.has(key)) {
                        mergedEnabledClauses.push(newEc);
                    }
                }

                await updateDraftTopicClauses(draftId, {
                    updates: [
                        {
                            topic_id: topicId,
                            enabled_clauses: mergedEnabledClauses,
                        },
                    ],
                });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: adminDraftQueryKeys.detail(draftId),
            });
            enqueueSnackbar(
                "Clause has been successfully added to the draft.",
                { variant: "success" }
            );
            handleClose();
        },
    });

    const { data, isFetching } = useQuery({
        queryKey: [
            "draft-add-clause",
            paginationModel.page,
            paginationModel.pageSize,
            filterModel,
        ],
        queryFn: () =>
            getAllClauses(
                paginationModel.page + 1,
                paginationModel.pageSize,
                filterModel
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
            reset();
            setSelectedClause(null);
        }
    }, [data, reset]);

    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    const handleRowClick = (params: GridRowParams<IClause>) => {
        if (selectedClause?.id === params.row.id) {
            setSelectedClause(null);
        } else {
            setSelectedClause(params.row);
        }
    };

    const handleClose = () => {
        onClose();
        setSelectedClause(null);
        reset();
        resetFilterModel();
        setPaginationModel({ page: 0, pageSize: 10 });
    };

    const selectedClauseHasTopic =
        selectedClause && selectedClause.topics.length > 0;
    const selectedClauseTopic = selectedClauseHasTopic
        ? selectedClause.topics[0]
        : null;

    const axiosError = mutationError as IApiAxiosError;

    const footerText = () => {
        if (axiosError) {
            return (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ fontSize: "0.75rem" }}
                >
                    {axiosError.response?.data?.detail ||
                        axiosError.message ||
                        "Failed to add clause to the draft."}
                </Typography>
            );
        } else if (selectedClause) {
            if (selectedClauseHasTopic) {
                return (
                    <Typography variant="caption" color="text.primary">
                        Adding &quot;{selectedClause.title}&quot; will{" "}
                        {assignedTopicIds.has(selectedClauseTopic!.id)
                            ? `enable it in the existing "${selectedClauseTopic!.name}" topic.`
                            : `also add the "${selectedClauseTopic!.name}" topic.`}
                    </Typography>
                );
            } else {
                return (
                    <Typography variant="caption" color="warning.dark">
                        This clause has no assigned topic and cannot be added.
                    </Typography>
                );
            }
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Add Clause to Draft"
            subtitle="Select a clause to add. Its topic will be automatically included."
            maxWidth="md"
            isLoading={mutationPending}
            actions={[
                {
                    label: "Add Clause",
                    onClick: () => mutate(),
                    variant: "contained",
                    color: "primary",
                    disabled: !selectedClauseHasTopic || mutationPending,
                },
            ]}
            footerText={footerText()}
        >
            <Stack spacing={2}>
                <Stack height={400}>
                    <Table
                        loading={isFetching}
                        columns={columns}
                        rows={rows}
                        onRowClick={handleRowClick}
                        getRowClassName={(params) =>
                            params.row.id === selectedClause?.id
                                ? "Mui-selected"
                                : ""
                        }
                        pageSizeOptions={[10, 25, 50]}
                        paginationModel={paginationModel}
                        rowCount={rowCount}
                        onPaginationModelChange={setPaginationModel}
                        paginationMode="server"
                        filterMode="server"
                        sortingMode="server"
                        onFilterModelChange={handleFilterModelChange}
                        onSortModelChange={handleFilterModelChange}
                        showToolbar
                        slots={{
                            toolbar: TableFilterToolbar,
                        }}
                        sx={{
                            "& .MuiDataGrid-row": {
                                cursor: "pointer",
                            },
                            "& .MuiDataGrid-row.Mui-selected": {
                                bgcolor: "action.selected",
                            },
                            "& .MuiDataGrid-row.Mui-selected:hover": {
                                bgcolor: "action.selected",
                            },
                        }}
                    />
                </Stack>
            </Stack>
        </Modal>
    );
};
